define('core/utils/objectExpander',[
    'underscore',

    'core/UniqueModel',
    'core/models/Channel',
    'core/models/Thread',
], function (
    _,

    UniqueModel,
    Channel,
    Thread
) {
    'use strict';

    /**
     * A set of functions to expand references to other models in a model
     *
     * Contrived example --
     *
     * 1. given an object mapping
     * objects = {
     *     'Thread?456': {id: 456, forum: 'Forum?shortnameExample', url: 'example.com'},
     *     'Forum?shortnameExample': {id: 'shortnameExample', name: 'Example'}
     * }
     *
     * 2. and a compact, serialized representation of a model
     * post = {
     *     id: 123,
     *     thread: 'Thread?456'
     * }
     *
     * 3. produce a fully expanded representation
     * post = {
     *     id: 123,
     *     thread: {
     *         id: 456,
     *         forum: {
     *             id: 'shortnameExample',
     *             name: 'Example'
     *         }
     *         url: 'example.com'
     *     }
     * }
     */
    return {
        // Store local reference to model(s) so that inheriting expanders can overwite
        // model classes with project-specific models
        Channel: Channel,
        Thread: Thread,

        /**
         * Returns an object's attributes, given either the attributes themselves
         * or the id with which to lookup the attributes in the given objects mapping.
         *
         * @param  {Object} objects - Mapping of model type/id to model attributes
         * @param  {object/string} attrs   - Either the object's attributes or the
         *                                   type/id string to lookup the attributes
         *                                   in the objects mapping
         * @returns {Object}         - The object's attributes
         */
        parseObject: function (objects, attrs) {
            return _.isString(attrs) ? objects[attrs] : attrs;
        },

        /**
         * Creates and returns a new unique thread model.
         * Looks up and passes the appropriate forum attributes to the thread
         * constructor so it doesn't need a separate fetch call to populate its
         * forum.
         *
         * @param  {Object} objects  - Mapping of model type/id to model attributes
         * @param  {string} threadAttrs - either the thread attributes or the type/id
         *                                string to lookup the thread attributes
         *
         * @returns {Thread}          - A Thread UniqueModel with a Forum model set as the
         *                             forum
         */
        buildThread: function (objects, threadAttrs) {
            if (threadAttrs instanceof this.Thread)
                return threadAttrs;

            threadAttrs = this.parseObject(objects, threadAttrs);

            // If not an object, threadAttrs.author equals either
            // 'auth.User?id=<numeric_id>' or '<numeric_id>'
            if (_.isString(threadAttrs.author)) {
                var author = threadAttrs.author.replace('auth.User?id=', '');
                // it's important (e.g., for API calls) that if 'auth.User?id=' + author
                // is not in the objects mapping that threadAttrs.author === '<numeric_id>'
                threadAttrs.author = objects['auth.User?id=' + author] || author;
            }

            return new UniqueModel(
                this.Thread,
                threadAttrs,
                {
                    forum: this.parseObject(objects, threadAttrs.forum),
                    author: threadAttrs.author,
                }
            );
        },

        buildChannel: function (objects, channelAttrs) {
            if (channelAttrs instanceof this.Channel)
                return channelAttrs;

            channelAttrs = this.parseObject(objects, channelAttrs);

            return new UniqueModel(
                this.Channel,
                channelAttrs
            );
        },
    };
});

